/*
 * The knowledge-base surface.
 *
 * Every route here is in the committed contract, and every type is read off the
 * generated `paths` map or `components`, so a change upstream stops this file
 * compiling instead of quietly mistyping a response. The wiki, history, links and
 * operations routes were once ahead of the published spec and hand-typed in this
 * file; the contract has since landed and the generated shapes replaced them.
 */
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiUpload,
  apiUrl,
  getActiveScope,
  type Json200,
  type Json201,
} from './client.ts'
import type { components, paths } from './schema.ts'

const ROOT = '/v1/knowledge-bases'

function base(kbId: string): string {
  return `${ROOT}/${encodeURIComponent(kbId)}`
}

/**
 * A path with each segment escaped but the separators intact.
 *
 * `raw/{file_path}` is a catch-all parameter: `assets/figure.png` is one value
 * containing a slash. Escaping it whole would turn the slash into `%2F`, which
 * some proxies reject and others decode after routing has already happened.
 */
function encodePath(filePath: string): string {
  return filePath.split('/').map(encodeURIComponent).join('/')
}

// ═══════════════════════════════════════════════════════════════════════════════
// 1. Published contract — types generated, not written
// ═══════════════════════════════════════════════════════════════════════════════

export type KnowledgeBase = components['schemas']['KnowledgeBaseResponse']
export type KnowledgeBaseList = Json200<paths['/v1/knowledge-bases']['get']>
export type CreateKnowledgeBase = components['schemas']['CreateKnowledgeBaseRequest']
export type UpdateKnowledgeBase = components['schemas']['UpdateKnowledgeBaseRequest']
export type KnowledgeBaseLimits = Json200<
  paths['/v1/knowledge-bases/{kb_id}/limits']['get']
>
export type KnowledgeBaseLimit = components['schemas']['KnowledgeBaseLimitResponse']
export type KnowledgeBaseUsage = components['schemas']['KnowledgeBaseUsageResponse']
export type SourceList = Json200<paths['/v1/knowledge-bases/{kb_id}/raw']['get']>
export type SourceFile = components['schemas']['KnowledgeBaseSourceFile']
export type SourceUpload = Json201<paths['/v1/knowledge-bases/{kb_id}/raw']['post']>
export type SchemaRevisionList = Json200<
  paths['/v1/knowledge-bases/{kb_id}/schema/revisions']['get']
>
export type SchemaRevision = components['schemas']['SchemaRevisionResponse']
export type SchemaWrite = Json201<paths['/v1/knowledge-bases/{kb_id}/schema']['post']>

/** Envelope `?preview=true` returns for one source file. */
export interface SourcePreview {
  kind: 'text' | 'image' | 'binary' | string
  filename: string
  contentType: string
  text: string | null
}

export function listKnowledgeBases(includeArchived = false): Promise<KnowledgeBaseList> {
  return apiGet<KnowledgeBaseList>(ROOT, { include_archived: includeArchived })
}

export function createKnowledgeBase(body: CreateKnowledgeBase): Promise<KnowledgeBase> {
  return apiPost<KnowledgeBase>(ROOT, body)
}

export function getKnowledgeBase(kbId: string): Promise<KnowledgeBase> {
  return apiGet<KnowledgeBase>(base(kbId))
}

export function updateKnowledgeBase(
  kbId: string,
  body: UpdateKnowledgeBase,
): Promise<KnowledgeBase> {
  return apiPatch<KnowledgeBase>(base(kbId), body)
}

export function archiveKnowledgeBase(kbId: string): Promise<KnowledgeBase> {
  return apiPost<KnowledgeBase>(`${base(kbId)}/archive`)
}

export function getLimits(kbId: string): Promise<KnowledgeBaseLimits> {
  return apiGet<KnowledgeBaseLimits>(`${base(kbId)}/limits`)
}

export function listSources(kbId: string): Promise<SourceList> {
  return apiGet<SourceList>(`${base(kbId)}/raw`)
}

/** Upload one or more source files. The multipart field name is `files`. */
export function uploadSources(kbId: string, files: File[]): Promise<SourceUpload> {
  const form = new FormData()
  for (const file of files) form.append('files', file, file.name)
  return apiUpload<SourceUpload>(`${base(kbId)}/raw`, form)
}

export function previewSource(kbId: string, filePath: string): Promise<SourcePreview> {
  return apiGet<SourcePreview>(`${base(kbId)}/raw/${encodePath(filePath)}`, { preview: true })
}

/** An `<a href>` like the bundle URL, so the scope rides in the URL — see there. */
export function sourceDownloadUrl(kbId: string, filePath: string): string {
  const scopeId = getActiveScope()
  return apiUrl(
    `${base(kbId)}/raw/${encodePath(filePath)}`,
    scopeId ? { scope_id: scopeId } : undefined,
  )
}

export function deleteSource(kbId: string, filePath: string): Promise<void> {
  return apiDelete(`${base(kbId)}/raw/${encodePath(filePath)}`)
}

export function listSchemaRevisions(kbId: string, limit = 50): Promise<SchemaRevisionList> {
  return apiGet<SchemaRevisionList>(`${base(kbId)}/schema/revisions`, { limit })
}

export function writeSchema(kbId: string, bodyMd: string): Promise<SchemaWrite> {
  return apiPost<SchemaWrite>(`${base(kbId)}/schema`, { body_md: bodyMd })
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. The wiki, its history, its links, and the operations that write it
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * The wiki files every base is seeded with, so a fresh base is never blank: the
 * catalogue the agent navigates by, the narrative log it appends to, and the alias
 * registry that stops one thing becoming three pages.
 */
export const WIKI_INDEX_PATH = 'wiki/index.md'
export const WIKI_LOG_PATH = 'wiki/log.md'
export const WIKI_ALIASES_PATH = 'wiki/aliases.md'

/**
 * The page lifecycle values this UI knows how to render. The wire type is an open
 * string on purpose — an unfamiliar status renders as itself, not as an error.
 */
export type PageStatus = 'fresh' | 'stale' | 'orphaned' | 'unverified'

export type WikiIndex = Json200<paths['/v1/knowledge-bases/{kb_id}/wiki']['get']>
export type WikiPage = components['schemas']['WikiPageResponse']
export type WikiContent = components['schemas']['WikiContentResponse']

export type History = Json200<paths['/v1/knowledge-bases/{kb_id}/history']['get']>
export type HistoryOperation = components['schemas']['HistoryOpResponse']
export type OperationDiff = components['schemas']['OpDiffResponse']
export type DiffFile = components['schemas']['DiffFileResponse']
export type DiffHunk = components['schemas']['DiffHunkResponse']
export type DiffLine = components['schemas']['DiffLineResponse']
export type FrontmatterChange = components['schemas']['FrontmatterChangeResponse']
export type RevertStarted = components['schemas']['RevertResponse']

export type LinkGraph = Json200<paths['/v1/knowledge-bases/{kb_id}/links']['get']>
export type PageLinks = components['schemas']['PageLinksResponse']
export type BrokenLink = components['schemas']['BrokenLinkResponse']

export type OperationRequest = components['schemas']['StartOperationRequest']
/** `POST .../operations` answers 202: the operation is accepted, not finished. */
export type StartedOperation = components['schemas']['StartOperationResponse']

export type WikiWriteLease = components['schemas']['WikiWriteLeaseResponse']
export type RateUsage = components['schemas']['KnowledgeBaseRateUsageResponse']

export function getWikiIndex(kbId: string): Promise<WikiIndex> {
  return apiGet<WikiIndex>(`${base(kbId)}/wiki`)
}

export function getWikiContent(kbId: string, path: string): Promise<WikiContent> {
  return apiGet<WikiContent>(`${base(kbId)}/wiki/content`, { path })
}

export function getHistory(kbId: string): Promise<History> {
  return apiGet<History>(`${base(kbId)}/history`)
}

export function getOperationDiff(kbId: string, opId: string): Promise<OperationDiff> {
  return apiGet<OperationDiff>(`${base(kbId)}/history/${encodeURIComponent(opId)}/diff`)
}

export function revertOperation(kbId: string, opId: string): Promise<RevertStarted> {
  return apiPost<RevertStarted>(`${base(kbId)}/history/${encodeURIComponent(opId)}/revert`)
}

/**
 * The bundle download is an `<a href>`, not a fetch — the browser sends no
 * `X-Kb-Scope` header for it, so the BFF has nothing to place and the request
 * would silently run in the caller's default scope. The scope therefore rides
 * in the URL itself, spelled the way the contract declares for this route.
 */
export function gitBundleUrl(kbId: string): string {
  const scopeId = getActiveScope()
  return apiUrl(`${base(kbId)}/git/bundle`, scopeId ? { scope_id: scopeId } : undefined)
}

export function getLinks(kbId: string): Promise<LinkGraph> {
  return apiGet<LinkGraph>(`${base(kbId)}/links`)
}

export function startOperation(
  kbId: string,
  body: OperationRequest,
): Promise<StartedOperation> {
  return apiPost<StartedOperation>(`${base(kbId)}/operations`, body)
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. The conversations that belong to a base
// ═══════════════════════════════════════════════════════════════════════════════

export type SessionList = Json200<paths['/v1/sessions']['get']>

/**
 * The newest session recorded against this base, or null when it has none.
 *
 * This is what lets a transcript follow the user to another device: the session
 * id is remembered locally as a preference, and when this browser has none, the
 * API's own base→session pointer answers instead of rendering an empty chat.
 */
export async function latestSessionFor(kbId: string): Promise<string | null> {
  const sessions = await apiGet<SessionList>('/v1/sessions', {
    knowledge_base_id: kbId,
    limit: 1,
  })
  return sessions.items[0]?.id ?? null
}
